import React, { useState, useMemo, useRef } from 'react';
import {
  cn,
  AlertsAndNotifications,
  OnboardingHeader,
  DocumentUploadSection,
  Text,
  CheckIcon,
} from '@tasama/ui';
import { formatDate, useFileUploadProgress } from '@tasama/utils';
import {
  useWorkflowStatuses,
  useCompanyDetails,
} from '../../features/my-account/hooks/useMyAccountApi';
import { useTranslation } from '@tasama/i18n';

interface UploadedFile {
  id: string;
  fileName: string;
  fileSize: string;
  bytes?: number;
  progress: number;
  status: 'uploading' | 'complete' | 'error';
  file?: File;
}

interface DocumentType {
  id: string;
  label: string;
  lastReceived?: string;
  required: boolean;
  accepted?: boolean;
}

interface UploadResult {
  status: 'success' | 'failed';
  fileName: string;
}

export interface UploadResponse {
  failureCount: number;
  results?: UploadResult[];
  kybSubmitError?: {
    message: string;
  };
}

export interface CompanyDocumentsUploadHandle {
  submit: () => Promise<void>;
  canSubmit: () => boolean;
}

interface CompanyDocumentsUploadScreenProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onSubmit'
> {
  /** Callback when Submit button is clicked - receives uploaded files data */
  onSubmit?: (
    _uploadedFiles: Record<string, UploadedFile[]>
  ) => void | Promise<void>;
  /** Test ID for QA automation */
  testID?: string | undefined;
  /** Company type to determine upload restrictions */
  companyType:
    | 'INTERNATIONAL'
    | 'KSA_PRIVATE'
    | 'PIF_ESTABLISHED'
    | 'PIF_UNDER_DEVELOPMENT';
  /** Upload response data for error handling */
  uploadResponse?: UploadResponse;
  /** Flag indicating if there are file upload errors */
  hasFileUploadError?: boolean;
  /** Flag indicating if its KYB route or my-account route */
  isKyb?: boolean;
  /** Ref to expose submit handler */
  handleRef?: React.RefObject<CompanyDocumentsUploadHandle | null>;
  /** Callback to notify parent when submit state changes */
  onSubmitStateChange?: (_canSubmit: boolean) => void;
}

/**
 * Company Documents Upload Screen Component
 *
 * Displays the upload documents step for company profile editing.
 * Based on Figma design: https://www.figma.com/design/IcSV9qhrjLJh6Y3Q8F7lcr/Tasama-UI-UX-Design?node-id=4366-174388
 */
const CompanyDocumentsUploadScreen: React.FC<
  CompanyDocumentsUploadScreenProps
> = ({
  className,
  onSubmit,
  testID = 'company-documents-upload-screen',
  companyType,
  uploadResponse,
  hasFileUploadError,
  isKyb = false,
  handleRef,
  onSubmitStateChange,
  ...props
}) => {
  const { t } = useTranslation('portal');
  const { data: workflowStatuses } = useWorkflowStatuses();
  const { data: companyDetails } = useCompanyDetails();

  // TDF-1310 / TDF-1311: while a conversion is pending (International -> KSA post-TBL, or
  // PIF project -> PIF established) the client is establishing a new entity, so drive the document
  // set from the TARGET company type. This is route-independent (works via the /kyb task or the Edit CTA).
  const conversion = companyDetails?.conversion;
  const effectiveType = conversion?.pending
    ? conversion.targetCompanyType
    : companyType;

  const documentTypes = useMemo<DocumentType[]>(() => {
    const isPifUnderDevelopment = companyType === 'PIF_UNDER_DEVELOPMENT';
    const isConversionPending = conversion?.pending ?? false;

    const companyRegistrationDoc = {
      id: 'company-registration',
      label: t('documents.companyRegistrationCertificate'),
      lastReceived: formatDate(
        isPifUnderDevelopment
          ? (workflowStatuses?.kybSubmittedAt ?? '')
          : (workflowStatuses?.onboardingApprovedAt ?? '')
      ),
      required: isPifUnderDevelopment
        ? !workflowStatuses?.kybSubmittedAt
        : !workflowStatuses?.onboardingApprovedAt,
      accepted: isPifUnderDevelopment
        ? workflowStatuses?.kybStatus === 'APPROVED'
        : workflowStatuses?.onboardingStatus === 'APPROVED',
    };

    const baseDocuments = [companyRegistrationDoc];

    // A pre-conversion INTERNATIONAL company (no pending conversion) only shows the base document.
    const documents =
      effectiveType === 'INTERNATIONAL'
        ? baseDocuments
        : effectiveType === 'KSA_PRIVATE' ||
            effectiveType === 'PIF_ESTABLISHED' ||
            effectiveType === 'PIF_UNDER_DEVELOPMENT'
          ? [
              ...baseDocuments,
              {
                id: 'vat-certificate',
                label: t('documents.vatCertificate'),
                lastReceived: formatDate(
                  workflowStatuses?.kybSubmittedAt ?? ''
                ),
                required: !workflowStatuses?.kybSubmittedAt,
                accepted: workflowStatuses?.kybStatus === 'APPROVED',
              },
              {
                id: 'national-address',
                label: t('documents.nationalAddressId'),
                lastReceived: formatDate(
                  workflowStatuses?.kybSubmittedAt ?? ''
                ),
                required: !workflowStatuses?.kybSubmittedAt,
                accepted: workflowStatuses?.kybStatus === 'APPROVED',
              },
            ]
          : baseDocuments;

    // While a conversion is pending, none of the target company's documents have
    // actually been received/accepted yet — the workflow statuses still reflect the
    // OLD entity, so they must not be surfaced as received/accepted for the new one.
    if (isConversionPending) {
      return documents.map((document) => ({
        ...document,
        lastReceived: '',
        required: true,
        accepted: false,
      }));
    }

    return documents;
  }, [companyType, effectiveType, workflowStatuses, t, conversion?.pending]);

  const [uploadedFiles, setUploadedFiles] = useState<
    Record<string, UploadedFile[]>
  >({});
  const [uploading, setUploading] = useState<boolean>(false);
  const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);
  const [errorAlertMessage, setErrorAlertMessage] = useState<string>('');

  // Track progress values via ref to provide fresh reads to the hook callbacks
  // without relying on stale React state closures.
  const progressRef = useRef<Map<string, number>>(new Map());

  const { startProgress, cancelProgress } = useFileUploadProgress();

  const handleFilesSelected = (documentId: string, files: FileList) => {
    const fileArray = Array.from(files);

    const validFiles = fileArray.filter((file) => {
      const validTypes = [
        'application/pdf',
        'image/png',
        'image/jpeg',
        'image/jpg',
      ];
      const maxSize = 5 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        setErrorAlertMessage(t('companyDocuments.invalidFileType'));
        setShowErrorAlert(true);
        return false;
      }

      if (file.size > maxSize) {
        setErrorAlertMessage(t('companyDocuments.fileTooLarge'));
        setShowErrorAlert(true);
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;

    const newFiles: UploadedFile[] = validFiles.map((file, index) => ({
      id: `${documentId}-file-${Date.now()}-${index}`,
      fileName: file.name,
      fileSize: `${Math.round(file.size / 1024)} KB`,
      bytes: file.size,
      progress: 0,
      status: 'uploading' as const,
      file,
    }));

    // Initialise progress ref entries before kicking off intervals
    newFiles.forEach((f) => progressRef.current.set(f.id, 0));

    setUploadedFiles((prev) => ({
      ...prev,
      [documentId]: newFiles,
    }));

    newFiles.forEach((newFile, index) => {
      startProgress({
        fileId: newFile.id,
        delayMs: index * 100,
        getLatestProgress: () => progressRef.current.get(newFile.id),
        onUpdate: (progress, status) => {
          progressRef.current.set(newFile.id, progress);
          setUploadedFiles((prev) => ({
            ...prev,
            [documentId]: (prev[documentId] || []).map((f) =>
              f.id === newFile.id ? { ...f, progress, status } : f
            ),
          }));
        },
      });
    });
  };

  const handleFileRemove = (documentId: string, fileId: string) => {
    cancelProgress(fileId);
    progressRef.current.delete(fileId);
    setUploadedFiles((prev) => ({
      ...prev,
      [documentId]: prev[documentId]?.filter((f) => f.id !== fileId) || [],
    }));
  };

  const handleSubmit = React.useCallback(async () => {
    const requiredDocs = documentTypes.filter((doc) => doc.required);
    const missingDocs = requiredDocs.filter(
      (doc) => (uploadedFiles[doc.id]?.length ?? 0) === 0
    );

    if (missingDocs.length > 0) {
      const missingLabels = missingDocs.map((doc) => doc.label).join(', ');
      setErrorAlertMessage(
        t('companyDocuments.missingDocuments', { documents: missingLabels })
      );
      setShowErrorAlert(true);
      return;
    }

    setUploading(true);
    setShowErrorAlert(false);

    try {
      if (onSubmit) {
        await onSubmit(uploadedFiles);
      }

      if (!hasFileUploadError) {
        const updatedFiles: Record<string, UploadedFile[]> = {};
        Object.keys(uploadedFiles).forEach((docId) => {
          updatedFiles[docId] = (uploadedFiles[docId] ?? []).map((file) => ({
            ...file,
            progress: 100,
            status: 'complete' as const,
          }));
        });
        setUploadedFiles(updatedFiles);
      } else {
        throw new Error('Upload has failures!!');
      }
    } catch {
      if (hasFileUploadError && uploadResponse) {
        const failedFiles =
          uploadResponse.results?.filter(
            (result) => result.status === 'failed'
          ) || [];
        if (failedFiles.length > 0) {
          const failedFileNames = failedFiles
            .map((file) => file.fileName)
            .join(', ');
          setErrorAlertMessage(
            t('companyDocuments.uploadFailedFor', { files: failedFileNames })
          );
        } else {
          setErrorAlertMessage(t('companyDocuments.someFilesFailed'));
        }
      } else {
        setErrorAlertMessage(t('companyDocuments.uploadFailed'));
      }

      setShowErrorAlert(true);
    } finally {
      setUploading(false);
    }
  }, [
    documentTypes,
    uploadedFiles,
    onSubmit,
    hasFileUploadError,
    uploadResponse,
  ]);

  const canSubmit = React.useCallback(() => {
    const requiredDocs = documentTypes.filter((doc) => doc.required);
    const hasAllRequiredFiles = requiredDocs.every(
      (doc) => (uploadedFiles[doc.id]?.length ?? 0) > 0
    );
    const hasUploading =
      (Object.values(uploadedFiles) as UploadedFile[][]).some((files) =>
        files.some((file) => file.status === 'uploading')
      ) || uploading;
    return hasAllRequiredFiles && !hasUploading;
  }, [documentTypes, uploadedFiles, uploading]);

  // Expose submit handler to parent via ref
  React.useImperativeHandle(
    handleRef,
    () => ({
      submit: handleSubmit,
      canSubmit,
    }),
    [handleSubmit, canSubmit]
  );

  // Notify parent when submit state changes
  React.useEffect(() => {
    if (onSubmitStateChange) {
      onSubmitStateChange(canSubmit());
    }
  }, [uploadedFiles, uploading, documentTypes, onSubmitStateChange]);

  const handleErrorAlertClose = () => {
    setShowErrorAlert(false);
    setErrorAlertMessage('');
  };

  return (
    <div
      data-testid={testID}
      className={cn('flex flex-col w-full', className)}
      {...props}
    >
      {/* Error Alert */}
      {showErrorAlert && errorAlertMessage && (
        <AlertsAndNotifications
          variant="error"
          title={t('companyDocuments.uploadError')}
          description={errorAlertMessage}
          onClose={handleErrorAlertClose}
          showClose={true}
          testID={`${testID}-error-alert`}
          className="fixed top-8 left-1/2 transform -translate-x-1/2 z-notification w-full max-w-[600px] px-4"
        />
      )}

      {/* Header Section */}
      <OnboardingHeader
        icon="Upload"
        size="lg"
        title={t('companyDocuments.uploadTitle')}
        description={
          companyType === 'INTERNATIONAL'
            ? t('companyDocuments.descriptionInternational')
            : companyType === 'KSA_PRIVATE' ||
                companyType === 'PIF_ESTABLISHED' ||
                companyType === 'PIF_UNDER_DEVELOPMENT'
              ? t('companyDocuments.descriptionKsaPrivate')
              : t('companyDocuments.descriptionDefault')
        }
        testID={`${testID}-header`}
      />

      {/* Content Section */}
      <div className="space-y-8">
        <div className="space-y-6">
          {documentTypes.map((document) => {
            const files = uploadedFiles[document.id] || [];
            const hasFiles = files.length > 0;
            const hasLastReceived =
              document.lastReceived && document.lastReceived !== '-';
            const isComplete = hasFiles || hasLastReceived;

            return (
              <div key={document.id} className="space-y-4">
                {/* Document Info */}
                <div className="flex items-center gap-4">
                  <CheckIcon
                    className="shrink-0"
                    state={isComplete ? 'completed' : 'required'}
                    testID={
                      testID
                        ? `${testID}-check-icon`
                        : 'document-upload-check-icon'
                    }
                  />
                  <Text
                    size="base"
                    weight="light"
                    color="default"
                    className="text-fg-primary"
                  >
                    {document.label}
                    {document.lastReceived && document.lastReceived !== '-' && (
                      <span className="text-fg-quaternary">
                        {' '}
                        {t('companyDocuments.lastReceived', {
                          date: document.lastReceived,
                        })}
                      </span>
                    )}
                  </Text>
                </div>

                {/* Hide the CR upload only for an established company doing a KYB (its CR came from
                    onboarding). A pending conversion (International / PIF project) has no onboarding CR,
                    so it must always be able to upload a fresh one. */}
                {(document.id !== 'company-registration' ||
                  !isKyb ||
                  conversion?.pending) && (
                  <DocumentUploadSection
                    files={files}
                    onFilesSelected={(fileList) =>
                      handleFilesSelected(document.id, fileList)
                    }
                    onFileRemove={(fileId) =>
                      handleFileRemove(document.id, fileId)
                    }
                    disableRemove={uploading}
                    accept=".pdf,.png,.jpg,.jpeg"
                    supportingText={t('companyDocuments.supportingText')}
                    testID={`${testID}-${document.id}-upload`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

CompanyDocumentsUploadScreen.displayName = 'CompanyDocumentsUploadScreen';

export { CompanyDocumentsUploadScreen };
export type { CompanyDocumentsUploadScreenProps, UploadedFile, DocumentType };
